import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useLlmCosts } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Skeleton } from 'src/components/ui/Skeleton';
import type { EditorialLlmCostBucket, EditorialLlmCostDay, EditorialLlmCosts } from 'src/types/editorial';

/**
 * Answers: what is the machine costing, and which part of it is spending the money.
 *
 * Until 2026-09-23 the answer lived in a file on the pipeline box — a machine that is stopped most
 * of the day and replaced on every deploy — so the only reading anyone had was the provider's
 * monthly invoice, for the whole account, after the fact. Every call now records its model, its
 * tokens, the slice of prompt the provider served from cache and what it cost, and this is that
 * ledger read back.
 *
 * Grouped by stage as well as by model because that is the decision the screen exists to support.
 * Production ran extraction, judging and verdicts on the same frontier model: a stage view is what
 * shows that one call per 90 seconds of broadcast, not the drafting, is where the tokens go.
 */
const WINDOWS = [7, 30, 90] as const;

export function CostsWorkspace() {
  const [days, setDays] = useState<number>(30);
  const costs = useLlmCosts(days);

  return (
    <div className="page-medium">
      <PageHeader title="Costos">
        Lo que la caja gastó en modelos: una fila por llamada, con su etapa, su modelo, sus tokens y
        su precio. El caché del proveedor se cobra aparte y mucho más barato, así que se muestra por
        separado: si una etapa de redacción marca 0% de caché, el orden de su prompt se rompió.
      </PageHeader>

      <div className="costs__windows" role="group" aria-label="Periodo">
        {WINDOWS.map(window => (
          <button
            key={window}
            type="button"
            className="costs__window"
            aria-pressed={days === window}
            onClick={() => setDays(window)}
          >
            {window} días
          </button>
        ))}
      </div>

      {costs.isPending && <Skeleton variant="rows" count={4} />}
      {costs.isError && <LoadFailure title="No se pudieron cargar los costos" error={costs.error} onRetry={costs.refetch} />}
      {costs.data && <CostsReport costs={costs.data} />}
    </div>
  );
}

function CostsReport({ costs }: { costs: EditorialLlmCosts }) {
  const { totals } = costs;

  if (totals.calls === 0) {
    return (
      <p className="costs__empty">
        No hay llamadas registradas en este periodo. La caja escribe una fila por llamada cuando
        procesa una transmisión; si estuvo apagada, no hay nada que cobrar.
      </p>
    );
  }

  return (
    <>
      <dl className="alert-stats">
        <div className="alert-stats__card">
          <dt>Gasto del periodo</dt>
          <dd>{formatMoney(totals.costUsd)}</dd>
          <small>{totals.calls.toLocaleString('es-MX')} llamadas</small>
        </div>
        <div className="alert-stats__card">
          <dt>Tokens de entrada</dt>
          <dd>{formatTokens(totals.inputTokens)}</dd>
          <small>{formatShare(totals.cachedInputTokens, totals.inputTokens)} servidos desde caché</small>
        </div>
        <div className="alert-stats__card">
          <dt>Tokens de salida</dt>
          <dd>{formatTokens(totals.outputTokens)}</dd>
          <small>lo que escribieron los modelos</small>
        </div>
        <div className="alert-stats__card">
          <dt>Sin precio</dt>
          <dd data-tone={totals.unpricedCalls > 0 ? 'contradicted' : undefined}>{totals.unpricedCalls.toLocaleString('es-MX')}</dd>
          {/* Never counted as free: a model missing from the table is a gap in the lectura, and
              reading it as zero would understate the bill exactly where it is least expected. */}
          <small>llamadas con un modelo que la caja no sabe tarifar</small>
        </div>
      </dl>

      <DailySpend days={costs.byDay} />

      <CostTable
        title="Por etapa"
        caption="La etapa es la versión del prompt que hizo la llamada — es la que dice dónde se va el gasto."
        buckets={costs.byStage}
      />
      <CostTable title="Por modelo" caption="Un modelo caro en una etapa de extracción es el hallazgo que esta pantalla busca." buckets={costs.byModel} />
      <CostTable
        title="Por transmisión"
        caption="Lo que costó cada mañanera. Las llamadas fuera de una transmisión —pruebas, comandos sueltos— no aparecen aquí."
        buckets={costs.byStream}
        linkToStream
      />
    </>
  );
}

/**
 * The daily curve. Inline SVG and no charting dependency, the same argument `RelevanceCurve`
 * makes: this is a row of bars, and the smallest chart library in common use is larger than the
 * screen it would draw on.
 */
function DailySpend({ days }: { days: EditorialLlmCostDay[] }) {
  if (days.length === 0) return null;

  const peak = Math.max(...days.map(day => Number(day.costUsd)), 0.0001);

  return (
    <figure className="costs__chart">
      <figcaption className="costs__chartHead">
        Gasto por día · máximo {formatMoney(String(peak))}
      </figcaption>
      <div className="costs__bars">
        {days.map(day => (
          <div key={day.day} className="costs__bar" title={`${day.day} · ${formatMoney(day.costUsd)} · ${day.calls} llamadas`}>
            <div className="costs__barFill" style={{ height: `${Math.max((Number(day.costUsd) / peak) * 100, 2)}%` }} />
            <span className="costs__barDay">{day.day.slice(5)}</span>
          </div>
        ))}
      </div>
    </figure>
  );
}

function CostTable({
  title,
  caption,
  buckets,
  linkToStream = false,
}: {
  title: string;
  caption: string;
  buckets: EditorialLlmCostBucket[];
  linkToStream?: boolean;
}) {
  if (buckets.length === 0) return null;

  return (
    <section className="costs__section">
      <h2 className="costs__title">{title}</h2>
      <p className="costs__caption">{caption}</p>
      <table className="costs__table">
        <thead>
          <tr>
            <th scope="col">{title.replace('Por ', '')}</th>
            <th scope="col">Llamadas</th>
            <th scope="col">Entrada</th>
            <th scope="col">Caché</th>
            <th scope="col">Salida</th>
            <th scope="col">Costo</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map(bucket => (
            <tr key={bucket.key}>
              <th scope="row">
                {linkToStream ? <Link to={`/stream/${encodeURIComponent(bucket.key)}`}>{bucket.key}</Link> : bucket.key}
              </th>
              <td className="mono">{bucket.calls.toLocaleString('es-MX')}</td>
              <td className="mono">{formatTokens(bucket.inputTokens)}</td>
              <td className="mono">{formatShare(bucket.cachedInputTokens, bucket.inputTokens)}</td>
              <td className="mono">{formatTokens(bucket.outputTokens)}</td>
              <td className="mono">{formatMoney(bucket.costUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Two decimals once the amount is readable as money, four while it is still fractions of a cent. */
function formatMoney(amount: string): string {
  const value = Number(amount);
  return `$${value >= 0.01 ? value.toFixed(2) : value.toFixed(4)}`;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}k`;
  return String(tokens);
}

function formatShare(part: number, whole: number): string {
  if (!whole) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}
